import React, { useEffect, useRef, useState } from 'react';
import { LedSnapshot, readLedSnapshot, subscribeLed } from '../services/customerDisplay';
import { usePos } from '../context/PosContext';
import './LedDisplay.css';

// Customer Display (web stand-in for the Android serial LED pole display,
// activity_led_control2.xml). Mirrors the live bill broadcast by the Bill
// screen: open this page in a second window, drag it onto the customer-facing
// monitor and hit the fullscreen button. Idle shows a welcome clock; a
// settled bill shows a thank-you for a few seconds before going idle.

const PAID_HOLD_MS = 20_000;

const LedDisplay: React.FC = () => {
  const { config } = usePos();
  const [snap, setSnap] = useState<LedSnapshot | null>(() => readLedSnapshot());
  const [now, setNow] = useState(new Date());
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => subscribeLed(setSnap), []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Keep the newest line in view as items are added.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [snap?.lines.length]);

  const goFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.();
  };

  const cur = snap?.currency ?? config.currencySymbol;
  const money = (n: number) => `${cur} ${n.toFixed(2)}`;
  const storeName = snap?.storeName || config.storeName || 'EPOS';

  // The thank-you screen holds for a few seconds, then falls back to idle.
  const paidExpired =
    snap?.state === 'paid' && now.getTime() - new Date(snap.updatedAt).getTime() > PAID_HOLD_MS;
  const state: LedSnapshot['state'] =
    !snap || paidExpired ? 'idle' : snap.state;

  return (
    <div className="ld-page">
      <header className="ld-header">
        <span className="ld-store">{storeName}</span>
        <span className="ld-clock">
          {now.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
          {'  ·  '}
          {now.toLocaleTimeString()}
        </span>
        <button className="ld-fs-btn" onClick={goFullscreen} title="Toggle fullscreen">⛶</button>
      </header>

      {state === 'billing' && snap && (
        <div className="ld-billing">
          <div className="ld-items" ref={listRef}>
            <div className="ld-line ld-line-head">
              <span className="ld-c-name">ITEM</span>
              <span className="ld-c-qty">QTY</span>
              <span className="ld-c-rate">RATE</span>
              <span className="ld-c-amt">AMOUNT</span>
            </div>
            {snap.lines.map((l, i) => (
              <div key={i} className={`ld-line ${i === snap.lines.length - 1 ? 'latest' : ''}`}>
                <span className="ld-c-name">{l.name}</span>
                <span className="ld-c-qty">{l.quantity}</span>
                <span className="ld-c-rate">{l.unitPrice.toFixed(2)}</span>
                <span className="ld-c-amt">{l.total.toFixed(2)}</span>
              </div>
            ))}
          </div>

          <aside className="ld-total-pane">
            <div className="ld-total-label">TOTAL</div>
            <div className="ld-total-amount">{money(snap.finalTotal)}</div>
            <div className="ld-total-meta">
              <span>Items: {snap.lines.length}</span>
              <span>Qty: {snap.totalQty}</span>
            </div>
            {snap.discount > 0 && (
              <div className="ld-savings">You save {money(snap.discount)}</div>
            )}
          </aside>
        </div>
      )}

      {state === 'paid' && snap && (
        <div className="ld-center">
          <div className="ld-thanks">THANK YOU!</div>
          {snap.paidTotal !== undefined && (
            <div className="ld-paid-amount">{money(snap.paidTotal)}</div>
          )}
          {snap.paidBillNo !== undefined && (
            <div className="ld-paid-bill">Bill No: {snap.paidBillNo}</div>
          )}
          <div className="ld-visit">VISIT AGAIN</div>
        </div>
      )}

      {state === 'idle' && (
        <div className="ld-center">
          <div className="ld-idle-clock">
            {now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="ld-welcome">WELCOME TO</div>
          <div className="ld-idle-store">{storeName}</div>
        </div>
      )}
    </div>
  );
};

export default LedDisplay;
